/**
 * @fileoverview Hook to detect and provide current user's permissions.
 *
 * Provides general permission information for the current user. For UI visibility
 * flags and resource-specific checks, use UseHasPermission hook.
 */

import { BusinessObj } from "auth";
import type { UserRole } from "models/userRole";
import {
	OBJECT_ID_ASRS_LOCK,
	OBJECT_ID_ASRS_UNLOCK,
	OBJECT_ID_CREATE_CONTAINMENT,
	OBJECT_ID_HOLD_CONTAINMENT,
	OBJECT_ID_MANAGE_USER,
	ROLE_NAME_SECTOR_ADMIN,
} from "pages/Users/constants";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { UserSlice } from "store";

/**
 * Type for accessing protected fields from UserPermissionDTO via BusinessObj.
 * BusinessObj extends UserPermissionDTO which has protected readonly fields.
 */
type userPermissionFields = {
	readonly plantId: string;
	readonly plantName: string;
	readonly sectorName: string;
};

/**
 * Plant that the current user can manage, extracted from permissions.
 */
export interface ManageablePlant {
	id: string;
	name: string;
	sectorId: string;
	sectorName: string;
}

/**
 * Sector where the current user has a given object permission (e.g. S1, C1).
 * Derived from permissions API; use getAccessibleSectorsForObject(objectId).
 */
export interface AccessibleSectorForObject {
	id: string;
	name: string;
}

/**
 * Permission detection result for the current user.
 */
export interface CurrentUserPermissions {
	isSectorAdmin: boolean;
	isPlantUsersAccessManager: boolean;
	allAccessibleSectorNames: string[];
	accessibleSectorIdsAsAdminSet: Set<string>;
	/** Returns sectors where the user has at least one permission for the given objectId (e.g. OBJECT_ID_MANAGE_USER, OBJECT_ID_CREATE_CONTAINMENT). */
	getAccessibleSectorsForObject: (objectId: string) => AccessibleSectorForObject[];
	manageablePlants: ManageablePlant[];
	createContainmentPlantIds: string[];
	createContainmentSectorNames: string[];
	asrsLockPlantIds: string[];
	asrsUnlockPlantIds: string[];
	getPlantIdsByObjectAndSector: (objectId: string, sectorName: string) => string[];
	hasPermissionForAllPlants: (
		impactedPlantIds: string[],
		objectId: string
	) => boolean;
}

/**
 * @returns Permission information for the current user.
 */
export function UseCurrentUserPermissions(): CurrentUserPermissions {
	const userRoles = useSelector(
		UserSlice.selectors.selectUserRoles
	) as UserRole[];
	const businessObjList = useSelector(
		UserSlice.selectors.selectBusinessObjList
	) as BusinessObj[];

	const getPlantIdsByObjectAndSector = useCallback(
		(objectId: string, sectorName: string): string[] => {
			return getPlantsByObjectId(businessObjList, objectId, sectorName);
		},
		[businessObjList]
	);

	const hasPermissionForAllPlants = useCallback(
		(impactedPlantIds: string[], objectId: string): boolean => {
			if (impactedPlantIds.length === 0) {
				return false;
			}

		const permissionPlantIds = getPlantsByObjectId(businessObjList, objectId);
		if (permissionPlantIds.length === 0) {
			return false;
		}

		const allowedPlantIds = new Set(permissionPlantIds);
			return impactedPlantIds.every((plantId) => allowedPlantIds.has(plantId));
		},
		[businessObjList]
	);

	return useMemo(() => {
		const accessibleSectorIdsAsAdminSet = new Set<string>();
		const allAccessibleSectorNamesSet = new Set<string>();

		for (const userRole of userRoles || []) {
			if (userRole.containmentSectorName) {
				allAccessibleSectorNamesSet.add(userRole.containmentSectorName);
			}
			if (
				userRole.roleName === ROLE_NAME_SECTOR_ADMIN &&
				userRole.containmentSectorId
			) {
				accessibleSectorIdsAsAdminSet.add(userRole.containmentSectorId);
			}
		}

		const isSectorAdmin = accessibleSectorIdsAsAdminSet.size > 0;
		const allAccessibleSectorNames = [...allAccessibleSectorNamesSet];

		const manageUserPermissions = (businessObjList || []).filter(
			(bo) => bo.GetBusinessObjTypeString() === OBJECT_ID_MANAGE_USER
		);

		const isPlantUsersAccessManager = manageUserPermissions.length > 0;

		const manageablePlants: ManageablePlant[] = manageUserPermissions.map((bo) => {
			const perm = bo as unknown as userPermissionFields;
			return {
				id: perm.plantId || "",
				name: perm.plantName || "",
				sectorId: "",
				sectorName: perm.sectorName || "",
			};
		});

		// Populate sector information for manageable plants from user roles
		// This is needed even when user is also a Sector Admin, as Plant Users Access Manager
		// permissions are plant-specific and may be in different sectors
		if (isPlantUsersAccessManager) {
			const plantIds = new Set(manageablePlants.map((p) => p.id));
			const userRolesByPlantId = new Map<string, UserRole>();
			for (const userRole of userRoles || []) {
				if (userRole.plantId && plantIds.has(userRole.plantId)) {
					userRolesByPlantId.set(userRole.plantId, userRole);
				}
			}

			for (const plant of manageablePlants) {
				const matchingUserRole = userRolesByPlantId.get(plant.id);
				if (matchingUserRole) {
					plant.sectorId = matchingUserRole.containmentSectorId || "";
					plant.sectorName = matchingUserRole.containmentSectorName || plant.sectorName;
				}
			}
		}

		const createContainmentPlantIds = getPlantsByObjectId(
			businessObjList,
			OBJECT_ID_CREATE_CONTAINMENT
		);

		const createContainmentSectorNames = getSectorNamesByObjectId(
			businessObjList,
			OBJECT_ID_CREATE_CONTAINMENT
		);

		const asrsLockPlantIds = getPlantsByObjectId(
			businessObjList,
			OBJECT_ID_ASRS_LOCK
		);

		const asrsUnlockPlantIds = getPlantsByObjectId(
			businessObjList,
			OBJECT_ID_ASRS_UNLOCK
		);

		const sectorNameToId = new Map<string, string>();
		for (const role of userRoles || []) {
			if (role.containmentSectorId && role.containmentSectorName) {
				sectorNameToId.set(role.containmentSectorName, role.containmentSectorId);
			}
		}

		const getAccessibleSectorsForObject = (objectId: string): AccessibleSectorForObject[] => {
			const sectorNames = getSectorNamesByObjectId(businessObjList, objectId);
			return [...new Set(sectorNames)]
				.map((name) => ({
					id: sectorNameToId.get(name) ?? "",
					name,
				}))
				.filter((s) => s.id && s.name)
				.sort((a, b) => a.name.localeCompare(b.name));
		};

		return {
			isSectorAdmin,
			isPlantUsersAccessManager,
			allAccessibleSectorNames,
			accessibleSectorIdsAsAdminSet,
			getAccessibleSectorsForObject,
			manageablePlants,
			createContainmentPlantIds,
			createContainmentSectorNames,
			asrsLockPlantIds,
			asrsUnlockPlantIds,
			getPlantIdsByObjectAndSector,
			hasPermissionForAllPlants,
		};
	}, [userRoles, businessObjList, getPlantIdsByObjectAndSector, hasPermissionForAllPlants]);
}

/**
 * Gets plant IDs for a specific business object type permission.
 *
 * @param businessObjList List of business objects to filter.
 * @param objectId Business object type ID to filter by.
 * @param sectorName Optional sector name to further filter by.
 * @returns Array of unique plant IDs.
 */
function getPlantsByObjectId(
	businessObjList: BusinessObj[],
	objectId: string,
	sectorName?: string
): string[] {
	let filteredBusinessObjs = (businessObjList || []).filter(
		(bo) => bo.GetBusinessObjTypeString() === objectId
	);

	if (sectorName) {
		filteredBusinessObjs = filteredBusinessObjs.filter(
			(bo) => bo.SectorName === sectorName
		);
	}

	return [
		...new Set(
			filteredBusinessObjs
				.map((bo) => {
					const perm = bo as unknown as { readonly plantId: string };
					return perm.plantId || "";
				})
				.filter(Boolean)
		),
	];
}

/**
 * Gets sector names for a specific business object type permission.
 *
 * @param businessObjList List of business objects to filter.
 * @param objectId Business object type ID to filter by.
 * @returns Array of unique sector names.
 */
function getSectorNamesByObjectId(
	businessObjList: BusinessObj[],
	objectId: string
): string[] {
	const filteredBusinessObjs = (businessObjList || []).filter(
		(bo) => bo.GetBusinessObjTypeString() === objectId
	);

	return [
		...new Set(
			filteredBusinessObjs
				.map((bo) => bo.SectorName)
				.filter(Boolean)
		),
	];
}